import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ENUM_ClientUserType } from '@api-new/common';
import { environment } from '@environments/environment';
import { Store, select } from '@ngrx/store';
import { AuthService } from '@shared/services/auth.service';
import { selectRedirectUrl } from '@shared/store/auth/auth.selectors';
import { selectClientUser } from '@shared/store/client-user/client-user.selectors';
import { selectCompanyNetworkId } from '@shared/store/company/company.selectors';
import dayjs from 'dayjs';
import { ButtonDirective } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { filter, firstValueFrom, forkJoin, take } from 'rxjs';

@Component({
  selector: 'cp-handle-app-open',
  imports: [Dialog, ButtonDirective],
  templateUrl: './handle-app-open.component.html',
  styleUrls: ['./handle-app-open.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HandleAppOpenComponent implements AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthService);
  private readonly globalStore = inject(Store);

  protected visible = false;

  ngAfterViewInit(): void {
    this.showRedirectToApp();
  }

  showRedirectToApp(): void {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInApp = environment.mobileApp?.isMobileApp;

    if (!isIOSDevice || isInApp || this.userContinuedToWebInLastDays()) return;
    forkJoin({
      user: this.globalStore.pipe(select(selectClientUser), filter(Boolean), take(1)),
      networkId: this.globalStore.pipe(select(selectCompanyNetworkId), filter(Boolean), take(1)),
    }).subscribe(({ user, networkId }) => {
      const isLandlord = user.clientUserType === ENUM_ClientUserType.CLIENT_USER_TYPE_LANDLORD;
      const isMabUser = networkId === MAB_NETWORK_ID;

      if (isLandlord && isMabUser) {
        this.visible = true;
        this.cdr.markForCheck();
      }
    });
  }

  cancel(): void {
    this.visible = false;
    localStorage.setItem('LAST_CONTINUE_IN_BROWSER', dayjs().toISOString());
  }

  userContinuedToWebInLastDays(): boolean {
    const lastContinue = localStorage.getItem('LAST_CONTINUE_IN_BROWSER');
    return dayjs().diff(lastContinue, 'days') < 10;
  }

  async openApp(): Promise<void> {
    const token = this.authService.token();
    const redirectUrl = await firstValueFrom(this.globalStore.pipe(select(selectRedirectUrl), take(1)));
    this.visible = false;
    const start = Date.now();

    const appLink = `mymablandlord://auth?token=${token}&link=${redirectUrl}`; // custom URL scheme

    // opens the app if is installed
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = appLink;
    document.body.appendChild(iframe);

    const timeout = setTimeout(() => {
      const elapsed = Date.now() - start;
      if (elapsed < 2000) {
        window.location.href = APPLE_STORE_URL;
      }
    }, 1500);

    setTimeout(() => {
      document.body.removeChild(iframe);
      clearTimeout(timeout);
    }, 2000);
  }
}

const MAB_NETWORK_ID = '1';
const APPLE_STORE_URL = 'https://apps.apple.com/gb/app/my-mab-landlord/id6751134847';
